import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaWarehouse, FaShieldAlt, FaBoxes, FaTruck, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { getWarehouses } from '../services/api';
import { Link } from 'react-router-dom';
import Loader from '../components/Loader';
import Reveal from '../components/Reveal';
import { gridContainer, gridItem, cardHover, heroContainer, viewportOnce } from '../components/motionPresets';
import { useT } from '../i18n/LanguageContext';
import './Warehousing.css';

const T = {
    en: {
        heroTitle: 'Warehousing Solutions',
        heroSubtitle: 'Secure storage and efficient logistics for your goods',
        introTitle: 'Why Our Warehousing?',
        introSubtitle: "Ensuring timely delivery and safe storage shouldn't be a struggle. We understand the complexities of logistics and offer solutions that empower you to operate seamlessly.",
        feature1Title: 'Efficient Warehousing',
        feature1Text: 'Proper warehousing is the backbone of effective logistics. We emphasize essential principles like inventory management and space optimization, helping you maximize operational efficiency and keep costs down.',
        feature2Title: 'Safety & Security First',
        feature2Text: 'Your products deserve the best protection. With our expansive warehouses in Yaounde and Douala, we ensure all goods are stored securely with strict safety protocols to prevent damage and loss.',
        feature3Title: 'Streamlined Transit',
        feature3Text: 'We know that timely deliveries are critical. Our strategic transit solutions complement warehousing operations, ensuring your customers receive their goods promptly and reliably.',
        facilitiesTitle: 'Our Facilities',
        loadingFacilities: 'Loading facilities',
        capacity: (value) => `Capacity: ${value}`,
        amenitySecurity: '24/7 Security Monitoring',
        amenityClimate: 'Climate Controlled',
        amenityInventory: 'Inventory Management',
        amenityAccess: 'Easy Access & Loading',
        ctaTitle: 'Need Warehousing Solutions?',
        ctaText: 'Contact us to discuss your storage needs and get a customized plan.',
        ctaButton: 'Contact Us',
    },
    fr: {
        heroTitle: "Solutions d'entreposage",
        heroSubtitle: 'Stockage securise et logistique efficace pour vos marchandises',
        introTitle: 'Pourquoi notre entreposage ?',
        introSubtitle: "Assurer une livraison ponctuelle et un stockage sur ne devrait pas etre un casse-tete. Nous comprenons la complexite de la logistique et proposons des solutions qui vous permettent d'operer en toute fluidite.",
        feature1Title: 'Entreposage efficace',
        feature1Text: "Un entreposage adapte est le socle d'une logistique performante. Nous mettons l'accent sur des principes essentiels tels que la gestion des stocks et l'optimisation de l'espace, vous aidant a maximiser votre efficacite operationnelle et a maitriser vos couts.",
        feature2Title: "Securite et surete avant tout",
        feature2Text: "Vos produits meritent la meilleure protection. Grace a nos vastes entrepots a Yaounde et Douala, nous garantissons un stockage securise de toutes les marchandises, avec des protocoles de securite stricts afin de prevenir les dommages et les pertes.",
        feature3Title: 'Transit optimise',
        feature3Text: "Nous savons que la ponctualite des livraisons est essentielle. Nos solutions de transit strategiques completent les operations d'entreposage, garantissant a vos clients une reception de leurs marchandises rapide et fiable.",
        facilitiesTitle: 'Nos installations',
        loadingFacilities: 'Chargement des installations',
        capacity: (value) => `Capacite : ${value}`,
        amenitySecurity: 'Surveillance de securite 24h/24 et 7j/7',
        amenityClimate: 'Climatisation controlee',
        amenityInventory: 'Gestion des stocks',
        amenityAccess: 'Acces et chargement faciles',
        ctaTitle: "Besoin de solutions d'entreposage ?",
        ctaText: 'Contactez-nous pour discuter de vos besoins de stockage et obtenir un plan personnalise.',
        ctaButton: 'Contactez-nous',
    },
};

function Warehousing() {
    const t = useT(T);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getWarehouses()
            .then(res => setWarehouses(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="warehousing-page">
            <motion.section className="page-hero" initial="hidden" animate="show" variants={heroContainer}>
                <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
            </motion.section>

            <section className="wh-intro">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.introTitle}</h2></Reveal>
                    <Reveal delay={0.08}><p className="section-subtitle">
                        {t.introSubtitle}
                    </p></Reveal>
                    <motion.div className="wh-features" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <motion.div className="wh-feature" variants={gridItem} whileHover={cardHover}>
                            <div className="wh-icon"><FaBoxes /></div>
                            <h3>{t.feature1Title}</h3>
                            <p>
                                {t.feature1Text}
                            </p>
                        </motion.div>
                        <motion.div className="wh-feature" variants={gridItem} whileHover={cardHover}>
                            <div className="wh-icon"><FaShieldAlt /></div>
                            <h3>{t.feature2Title}</h3>
                            <p>
                                {t.feature2Text}
                            </p>
                        </motion.div>
                        <motion.div className="wh-feature" variants={gridItem} whileHover={cardHover}>
                            <div className="wh-icon"><FaTruck /></div>
                            <h3>{t.feature3Title}</h3>
                            <p>
                                {t.feature3Text}
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section className="warehouses-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.facilitiesTitle}</h2></Reveal>
                    {loading && <Loader label={t.loadingFacilities} />}
                    {!loading && (
                    <motion.div className="warehouses-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {warehouses.map(wh => (
                            <motion.div key={wh.id} className="warehouse-card" variants={gridItem} whileHover={cardHover}>
                                {wh.image_url
                                    ? <img src={wh.image_url} alt={wh.name} className="wh-card-img" />
                                    : <div className="wh-card-icon"><FaWarehouse /></div>}
                                <h3>{wh.name}</h3>
                                <p className="wh-location">{wh.location}</p>
                                <p className="wh-capacity">{t.capacity(wh.capacity)}</p>
                                <p className="wh-desc">{wh.description}</p>
                                <ul className="wh-amenities">
                                    <li><FaCheckCircle /> {t.amenitySecurity}</li>
                                    <li><FaCheckCircle /> {t.amenityClimate}</li>
                                    <li><FaCheckCircle /> {t.amenityInventory}</li>
                                    <li><FaCheckCircle /> {t.amenityAccess}</li>
                                </ul>
                            </motion.div>
                        ))}
                    </motion.div>
                    )}
                </div>
            </section>

            <section className="cta-section">
                <Reveal className="cta-content" y={36}>
                    <h2>{t.ctaTitle}</h2>
                    <p>{t.ctaText}</p>
                    <Link to="/contact" className="btn btn-primary">
                        {t.ctaButton} <FaArrowRight />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}

export default Warehousing;
